import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  useTheme,
  Chip,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { useQuery } from "@apollo/client";
import { useParams, useLocation } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import { formatDateForInput, formatDate } from "../../../../utils/dateUtils";
import { GET_CHECK_ACTIVITY_HISTORY } from "../../../../apis/organization-apis/Infra-apis/InfraApis";
import { useAuditContext } from "../../../../hooks/useAuditContext";
import EvidenceLoadingSkeleton from "../common/EvidenceLoadingSkeleton";

const STATUS_CONFIG = [
  { key: "pass", name: "Passing", colorToken: "success" },
  { key: "fail", name: "Failing", colorToken: "error" },
  { key: "pending", name: "Pending", colorToken: "info" },
  { key: "disabled", name: "Disabled", colorToken: "warning" },
];

const getActivityLocalDateKey = (item) =>
  formatDateForInput(item?.run_time) || formatDateForInput(item?.date) || "";

const getActivityRunTimeMs = (item) => {
  const raw = item?.run_time ?? item?.date;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? 0 : t;
};

const pickLatestPerLocalDate = (items) => {
  const latestByKey = new Map();
  for (const item of items) {
    const key = item._localDateKey;
    if (!key) continue;
    const prev = latestByKey.get(key);
    if (!prev || getActivityRunTimeMs(item) >= getActivityRunTimeMs(prev)) {
      latestByKey.set(key, item);
    }
  }
  return Array.from(latestByKey.values());
};

const CheckActivityGraph = ({ activeCheckId, loading: externalLoading, rightAction = null }) => {
  const theme = useTheme();
  const location = useLocation();
  const { auditId: paramAuditId } = useParams();
  const auditId = location.state?.audit?.id || paramAuditId;
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  useEffect(() => {
    setSelectedPeriod(null);
  }, [activeCheckId]);

  const { auditStartDate, auditEndDate } = useAuditContext({ auditId });
  const auditStartKey = useMemo(
    () => formatDateForInput(auditStartDate),
    [auditStartDate]
  );
  const auditEndKey = useMemo(
    () => formatDateForInput(auditEndDate),
    [auditEndDate]
  );

  const auditDurationDays = useMemo(() => {
    if (!auditStartKey || !auditEndKey) return 0;
    const start = new Date(`${auditStartKey}T00:00:00`);
    const end = new Date(`${auditEndKey}T00:00:00`);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
    return Math.ceil((end - start) / 86400000);
  }, [auditStartKey, auditEndKey]);

  const availableMonths = useMemo(() => {
    if (!auditStartKey || !auditEndKey) return [];
    const months = [];
    const start = new Date(`${auditStartKey}T00:00:00`);
    const end = new Date(`${auditEndKey}T00:00:00`);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
    const cur = new Date(start.getFullYear(), start.getMonth(), 1);
    const last = new Date(end.getFullYear(), end.getMonth(), 1);
    while (cur <= last) {
      months.push({
        label: cur.toLocaleString("en-US", { month: "short", year: "numeric" }),
        value: `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}`,
      });
      cur.setMonth(cur.getMonth() + 1);
    }
    return months;
  }, [auditStartKey, auditEndKey]);

  const showMonthPicker = auditDurationDays > 30 && availableMonths.length > 0;

  const { data: activityData, loading: queryLoading } = useQuery(
    GET_CHECK_ACTIVITY_HISTORY,
    {
      variables: {
        check_id: Number(activeCheckId),
        audit_id: auditId,
        start_time: auditStartDate,
        end_time: auditEndDate,
      },
      skip: !activeCheckId,
      fetchPolicy: "cache-and-network",
    },
  );

  const loading = externalLoading || queryLoading;
  const activityHistory = activityData?.getCheckActivityHistory || [];

  /** Deduped by local day, sorted by date; used for default month and chart. */
  const activityPrepared = useMemo(() => {
    if (!activityHistory.length) {
      return { sorted: [], monthKeysWithData: new Set() };
    }
    const withLocalKeys = activityHistory
      .map((item) => ({
        ...item,
        _localDateKey: getActivityLocalDateKey(item),
        _displayDateTime: item?.run_time || item?.date || null,
      }))
      .filter((item) => item._localDateKey);

    const dedupedByDay = pickLatestPerLocalDate(withLocalKeys);
    const sorted = dedupedByDay.sort((a, b) =>
      a._localDateKey > b._localDateKey ? 1 : -1
    );
    const monthKeysWithData = new Set(
      sorted.map((item) => item._localDateKey.slice(0, 7))
    );
    return { sorted, monthKeysWithData };
  }, [activityHistory]);

  const activePeriod = useMemo(() => {
    if (!showMonthPicker) return null;
    if (selectedPeriod && availableMonths.some((m) => m.value === selectedPeriod)) {
      return selectedPeriod;
    }
    const { monthKeysWithData } = activityPrepared;
    for (let i = availableMonths.length - 1; i >= 0; i -= 1) {
      const v = availableMonths[i].value;
      if (monthKeysWithData.has(v)) return v;
    }
    return availableMonths.at(-1)?.value ?? null;
  }, [showMonthPicker, selectedPeriod, availableMonths, activityPrepared]);

  const chartData = useMemo(() => {
    const { sorted } = activityPrepared;
    if (!sorted.length) return { categories: [], series: {}, sorted: [] };

    let filtered = sorted;

    if (activePeriod && auditStartKey && auditEndKey) {
      const [y, m] = activePeriod.split("-").map(Number);
      if (y && m) {
        const monthStart = `${y}-${String(m).padStart(2, "0")}-01`;
        const monthEnd = formatDateForInput(new Date(y, m, 0));
        const start = monthStart < auditStartKey ? auditStartKey : monthStart;
        const end = monthEnd > auditEndKey ? auditEndKey : monthEnd;
        const result = sorted.filter((item) => {
          return item._localDateKey >= start && item._localDateKey <= end;
        });
        filtered = result;
      }
    }

    const series = Object.fromEntries(
      STATUS_CONFIG.map(({ key }) => [key, filtered.map((i) => (i.status === key ? 1 : 0))]),
    );

    return {
      categories: filtered.map(
        (item) => formatDate(item._displayDateTime || item._localDateKey) || "Invalid"
      ),
      series,
      sorted: filtered,
    };
  }, [activityPrepared, activePeriod, auditStartKey, auditEndKey]);

  const stats = useMemo(() => {
    const { sorted } = chartData;
    if (!sorted.length) return null;
    const total = sorted.length;
    const passCount = sorted.filter((i) => i.status === "pass").length;
    const failCount = sorted.filter((i) => i.status === "fail").length;
    const pendingCount = sorted.filter((i) => i.status === "pending").length;
    const disabledCount = sorted.filter((i) => i.status === "disabled").length;
    return {
      total,
      passCount,
      failCount,
      pendingCount,
      disabledCount,
      passRate: Math.round((passCount / total) * 100),
    };
  }, [chartData]);

  // Theme tokens
  const border = theme.customColors?.border?.main ?? theme.palette.divider;
  const statusColors = theme.customColors?.status ?? {};
  const getColor = (token, fallback) => statusColors?.[token]?.main ?? fallback;

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: "area", height: 280, toolbar: { show: false }, zoom: { enabled: false },
        fontFamily: theme.typography.fontFamily,
        animations: { enabled: true, easing: "easeinout", speed: 800 },
      },
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 2.5 },
      fill: {
        type: "gradient",
        gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0.1, stops: [0, 90, 100] },
      },
      colors: ["success", "error", "info", "warning"].map((t, i) =>
        getColor(t, ["#10B981", "#EF4444", "#3B82F6", "#F59E0B"][i]),
      ),
      xaxis: {
        categories: chartData.categories,
        labels: {
          rotate: -45, rotateAlways: false,
          style: { colors: theme.customColors?.text?.secondary ?? "#64748B", fontSize: "11px", fontWeight: 500 },
        },
        axisBorder: { show: true, color: border, height: 1 },
        axisTicks: { show: true, color: border },
        tooltip: { enabled: false },
      },
      yaxis: { show: false, max: 1 },
      grid: {
        borderColor: border, strokeDashArray: 4,
        xaxis: { lines: { show: false } }, yaxis: { lines: { show: true } },
        padding: { top: -10, right: 10, bottom: 0, left: 10 },
      },
      legend: {
        position: "top", horizontalAlign: "left", fontSize: "13px", fontWeight: 500,
        fontFamily: theme.typography.fontFamily,
        labels: { colors: theme.customColors?.text?.primary ?? theme.palette.text.primary },
        markers: { width: 10, height: 10, radius: 10, offsetX: -2 },
        itemMargin: { horizontal: 12, vertical: 8 },
        showForZeroSeries: true,
        showForNullSeries: true,
      },
      tooltip: {
        theme: theme.palette.mode === "dark" ? "dark" : "light",
        style: { fontSize: "12px", fontFamily: theme.typography.fontFamily },
        x: { show: true },
        y: { formatter: (val, { seriesIndex }) => (val === 1 ? STATUS_CONFIG[seriesIndex]?.name ?? "" : "") },
        marker: { show: true },
      },
    }),
    [chartData.categories, theme, border],
  );

  const series = STATUS_CONFIG.map(({ key, name }) => ({ name, data: chartData.series[key] ?? [] }));

  // Shared styles
  const containerSx = {
    bgcolor: "background.paper", borderRadius: "12px", border: `1px solid ${border}`,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)", display: "flex", flexDirection: "column",
  };
  const headerSx = {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    px: 3, py: 2, borderBottom: `1px solid ${border}`, bgcolor: "action.hover",
  };
  const titleSx = { fontWeight: 600, fontSize: "1.0625rem" };

  const EmptyState = ({ message }) => (
    <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
      <Typography variant="body2" color="text.secondary">{message}</Typography>
    </Box>
  );

  if (loading) return <EvidenceLoadingSkeleton />;

  if (!activityHistory.length) {
    return (
      <Box sx={{ ...containerSx, flex: 1 }}>
        <Box sx={headerSx}>
          <Typography variant="h6" sx={titleSx}>Daily Activity</Typography>
          {rightAction}
        </Box>
        <EmptyState message="No activity data available for this check" />
      </Box>
    );
  }

  return (
    <Box sx={{ ...containerSx, height: "100%", overflow: "hidden" }}>
      <Box sx={headerSx}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Typography variant="h6" sx={titleSx}>Daily Activity</Typography>

          {stats && (
            <Chip
              label={`${stats.passRate}% Pass Rate`}
              size="small"
              sx={{
                height: 24, fontSize: "0.75rem", fontWeight: 600,
                bgcolor: statusColors?.[stats.passRate >= 80 ? "success" : "warning"]?.bg,
                color: getColor(stats.passRate >= 80 ? "success" : "warning", stats.passRate >= 80 ? "#10B981" : "#F59E0B"),
              }}
            />
          )}

          {showMonthPicker && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={activePeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                sx={{
                  height: 32, fontSize: "0.8125rem", fontWeight: 500, borderRadius: "6px",
                  bgcolor: "background.paper",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
                }}
              >
                {availableMonths.map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        {rightAction}
      </Box>

      <Box sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        {chartData.categories.length > 0 ? (
          <Box sx={{ flex: 1 }}>
            <ReactApexChart options={chartOptions} series={series} type="area" height={280} />
          </Box>
        ) : (
          <EmptyState message="No activity data available for the selected period" />
        )}

        {stats && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 3,
              rowGap: 1.5,
              pt: 2,
              mt: 2,
              borderTop: `1px solid ${border}`,
            }}
          >
            {[
              { label: "Days Tracked", value: stats.total, color: "text.primary" },
              { label: "Passed", value: stats.passCount, color: getColor("success", "#10B981") },
              { label: "Failed", value: stats.failCount, color: getColor("error", "#EF4444") },
              { label: "Pending", value: stats.pendingCount, color: getColor("info", "#3B82F6") },
              { label: "Disabled", value: stats.disabledCount, color: getColor("warning", "#F59E0B") },
            ].map(({ label, value, color }) => (
              <Box key={label}>
                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>{label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color }}>{value}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CheckActivityGraph;